export default class TimezonesService {
  static get $inject() {
    return ['moment', 'permittedTimezones'];
  }

  constructor(moment, permittedTimezones) {
    this.moment = moment;
    this.setPermittedTimezones(permittedTimezones.timezones);
  }

  setPermittedTimezones(permittedTimezones) {
    this.guessedTimezone = this.moment.tz.guess();
    this.permittedTimezones = permittedTimezones;

    if (!this.permittedTimezones[this.guessedTimezone]) {
      this.permittedTimezones[this.guessedTimezone] = true;
    }

    this.data = this.getTimezones();
  }

  isPermittedTimezone(timezone) {
    return timezone.id in this.permittedTimezones;
  }

  isFullHourTimezone(timezone) {
    return timezone.nOffset % 60 === 0;// is offset a multitude of 60minutes
  }

  isGmtTimezone(timezone) {
    return timezone.id === 'Greenwich';
  }

  getTimezone(timezoneId) {
    return this.data.find(tz => tz.id === timezoneId) || this.moment.tz(timezoneId);
  }

  getTimezones() {
    return this.moment.tz.names()
      .map(zoneName => this.getTimezoneByZoneName(zoneName))
      .filter(timezone => this.isPermittedTimezone(timezone))
      .sort((first, second) => this.compareTimezones(first, second));
  }

  getTimezoneByZoneName(zoneName) {
    const timezone = this.moment.tz(zoneName);
    const nameString = zoneName.replace(/_/g, ' ');
    const offsetString = `UTC ${timezone.format('Z')}`;

    return Object.assign(timezone, {
      id: zoneName,
      name: nameString,
      offset: offsetString,
      nOffset: timezone.utcOffset(),
      abbr: timezone.format('z'),
      displayName: `(${offsetString}) ${nameString}`
    });
  }

  compareTimezones(a, b) {
    if (a.nOffset > b.nOffset) {
      return -1;
    } else if (a.nOffset < b.nOffset) {
      return 1;
    } else if (a.id > b.id) {
      return 1;
    } else if (a.id < b.id) {
      return -1;
    } else {
      return 0;
    }
  }

  toTimezone(date, timezone) {
    if (!date) {
      return undefined;
    } else if (!timezone) {
      return date;
    }

    const momentObj = this.moment(date).tz(timezone).toObject();

    return new Date(
      momentObj.years, momentObj.months, momentObj.date,
      momentObj.hours, momentObj.minutes, momentObj.seconds, momentObj.milliseconds
    );
  }

  fromTimezone(date, timezone) {
    if (!date) {
      return undefined;
    } else if (!timezone) {
      return date;
    }

    /* To update the model correctly, we need to figure out then apply the correct
       offset to the model. This calculation subtracts the selected timezone offset from the local
       timezone offset then adds it to the supplied date. Note: this works for both positive and
       negative offsets.

       Here's an example:

       1.) The user is in EST and has selected Jan 1, 12:00am EST in a date component
       2.) The user changes just the timezone to PST in the same date component. (The date component
           now reads Jan 1, 12:00am PST)
       3.) Since native javascript objects are always local time (in this example EST), we need to
           update the model accordingly. The following question needs to be answered:
           "Jan 1, 12:00am PST is what date/time in EST? - the answer is Jan 1, 3:00am EST"
       4.) The component still reads Jan 1, 12:00am PST (view-model) but the ngModel is now
           Jan 1, 3:00am EST

       Another example is a daterange controlling data shown in a chart. If the user changes just
       the timezone within the daterange component, the chart should change. This method allows that
       to happen.
   */

    const localOffset = date.getTimezoneOffset();
    const tzOffset = this.moment.tz.zone(timezone).offset(date.getTime());
    const offsetDelta = tzOffset - localOffset;
    const momentViewValue = this.moment(date);
    momentViewValue.add(offsetDelta, 'm');

    return momentViewValue.toDate();
  }

  findClosestFullHourTimezone(timezone) {
    const fullHours = Math.floor(timezone.nOffset / 60);
    const nearestOffset = fullHours * 60;

    return this.data.find(tz => tz.nOffset === nearestOffset);
  }

  getGmtTimezone() {
    return this.data.filter(tz => tz.id === 'Greenwich')[0] || this.moment.tz('Greenwich');
  }

  formatTimezone(timezone, format) {
    return this.data.find(tz => {
      const zoneObj = this.moment.tz(tz.id);
      return zoneObj.format(format) === timezone.format(format);
    });
  }

  getGuessedTimezone() {
    const guessedTimezoneAbbr = this.moment.tz(this.guessedTimezone).format('z');
    const localDate = new Date();

    let guessedTimezone = this.data.find(tz => tz.id === this.guessedTimezone);

    // If for some reason, the guessed timezone is not in the timezone set, attempt to retrieve a
    // timezone with the same abbreviation and timezone offset. Note, this shouldn't be a problem
    // because we inject the guessed timezone in the dataset if it doesn't already exist
    if (!guessedTimezone) {
      guessedTimezone = this.data.find(tz => {
        // This logic is needed because both India and Israel use the same timezone abbreviation
        if (tz.abbr === guessedTimezoneAbbr) {
          // comparing the offset is the best way to differentiate because the city name is used
          // in the full timezone name
          return this.moment.tz.zone(tz.id)
            .offset(localDate.getTime()) === localDate.getTimezoneOffset();
        }
      });

    }

    // If the guessed timezone is not in the list, use a timezone with the same offset
    if (!guessedTimezone) {
      guessedTimezone = this.data.find(tz => {
        return this.moment.tz.zone(tz.id)
          .offset(localDate.getTime()) === localDate.getTimezoneOffset();
      });
    }

    return guessedTimezone;
  }
}